import { useState, useRef, useCallback, useEffect } from 'react';
import ReactPlayer from 'react-player';

export const useVideoPlayer = () => {
  const playerRef = useRef<ReactPlayer>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [played, setPlayed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loaded, setLoaded] = useState(0);
  const [smoothProgress, setSmoothProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [hasDurationFromAPI, setHasDurationFromAPI] = useState(false);

  const handleSeek = useCallback(
    (newTime: number) => {
      if (playerRef.current && loaded > 0) {
        if (newTime < 0) {
          newTime = 0;
        }
        if (duration > 0 && newTime > duration) {
          newTime = duration;
          setPlaying(false);
          setPlayed(1);
          setSmoothProgress(1);
        } else if (newTime > loaded) {
          newTime = loaded;
        }
        playerRef.current.seekTo(newTime, 'seconds');

        // Update played immediately for better UX
        setPlayed(newTime);
        const newPlayed = newTime / duration;
        setSmoothProgress(newPlayed);
      }
    },
    [loaded, duration]
  );

  const handleError = useCallback((error: Error) => {
    setError('Failed to load video. Please try again later.');
    console.error('Video player error:', error);
  }, []);

  const handleEnded = useCallback(() => {
    setSmoothProgress(1);
    if (!hasDurationFromAPI) {
      setDuration(played);
      setHasDurationFromAPI(true);
    }
  }, [hasDurationFromAPI, played]);

  useEffect(() => {
    if (playing && duration > 0) {
      const updateProgress = () => {
        if (playerRef.current) {
          const currentTime = playerRef.current.getCurrentTime();
          const newProgress = duration > 0 ? currentTime / duration : 0;
          setSmoothProgress(Math.max(0, Math.min(1, newProgress)));
          animationFrameRef.current = requestAnimationFrame(updateProgress);
        }
      };

      animationFrameRef.current = requestAnimationFrame(updateProgress);
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [playing, duration]);

  const reset = useCallback(() => {
    setPlaying(false);
    setPlayed(0);
    setDuration(0);
    setSmoothProgress(0);
    setError(null);
    setIsReady(false);
    setHasDurationFromAPI(false);

    if (playerRef.current && typeof playerRef.current.seekTo === 'function') {
      playerRef.current.seekTo(0, 'seconds');
    }
  }, []);

  return {
    playing,
    played,
    duration,
    loaded,
    smoothProgress,
    error,
    isReady,
    playerRef,
    setPlaying,
    handleSeek,
    handleError,
    handleEnded,
    setIsReady,
    setPlayed,
    setLoaded,
    setDuration,
    setHasDurationFromAPI,
    hasDurationFromAPI,
    reset
  };
};
